import { FC, MouseEvent, ReactNode, useCallback, useState } from 'react';
import { Draggable } from '@carbon/react/icons';
import classnames from 'classnames';

import styles from './assets-slots-split-pane.module.scss';

const INITIAL_TOP_PCT = 50;
const MIN_TOP_PCT = 20;
const MAX_TOP_PCT = 80;

/** Props for {@link AssetsSlotsSplitPane}. */
export interface AssetsSlotsSplitPaneProps {
  /** Upper zone content (assets table in Step 1). */
  top: ReactNode;
  /** Lower zone content (slots table in Step 1). */
  bottom: ReactNode;
}

/**
 * Vertically split panel with a draggable handle between two stacked zones.
 * Top zone's height is stored as a percentage (0-100); bottom absorbs the
 * remainder via `flex: 1 1 auto`. Percentage scales gracefully on viewport
 * resize without carrying stale pixel values — same choice as the horizontal
 * precedent at `pages/documents/interpretations/page.tsx`.
 *
 * `isDragging` is state (not a ref) because the mock shows a `ns-resize`
 * cursor and `user-select: none` during a drag — CSS driven off the flag —
 * so the component must re-render when the flag flips. Only two flips per
 * drag (mousedown + mouseup), so the cost is trivial.
 *
 * Mouse listeners attach to the container rather than `window` — no listener
 * add/remove plumbing.
 *
 * @param {AssetsSlotsSplitPaneProps} props - top and bottom zone nodes
 * @return {JSX.Element} The split pane
 */
export const AssetsSlotsSplitPane: FC<AssetsSlotsSplitPaneProps> = ({
  top,
  bottom,
}) => {
  const [topHeightPct, setTopHeightPct] = useState(INITIAL_TOP_PCT);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const nextPct = ((event.clientY - rect.top) / rect.height) * 100;
      if (nextPct >= MIN_TOP_PCT && nextPct <= MAX_TOP_PCT) {
        setTopHeightPct(nextPct);
      }
    },
    [isDragging]
  );

  const startDrag = () => {
    setIsDragging(true);
  };

  const stopDrag = () => {
    setIsDragging(false);
  };

  return (
    <div
      className={classnames(styles.container, {
        [styles.containerDragging]: isDragging,
      })}
      onMouseMove={handleMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
    >
      <div className={styles.topZone} style={{ height: `${topHeightPct}%` }}>
        {top}
      </div>
      <div
        className={styles.handle}
        role="separator"
        aria-orientation="horizontal"
        onMouseDown={startDrag}
      >
        <Draggable className={styles.handleIcon} />
      </div>
      <div className={styles.bottomZone}>{bottom}</div>
    </div>
  );
};
